import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  Alert,
  AlertDescription,
  AlertIcon,
  useDisclosure,
  ModalFooter,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  FundingRequestEntitiesFragmentGQLType,
  ShareFundingComponentFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  errorToastAdminConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import {
  useMarkLenderShareComponentFundTransferDateMutation,
  GetResolvedLoanByIdDocumentGQLType,
} from '~/packages/loans/requests/loans.gql.generated';
import {
  GetDailyAccountActivityDocumentGQLType,
  GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
} from '~/packages/transactions/requests/transactions.gql.generated';

interface MarkLenderShareComponentFundedModalProps {
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
  fundingComponent: ShareFundingComponentFragmentGQLType;
  modalControls: ReturnType<typeof useDisclosure>;
}

export const MarkLenderShareComponentFundedModal = ({
  fundingRequest,
  fundingComponent,
  modalControls,
}: MarkLenderShareComponentFundedModalProps) => {
  const toast = useToast();

  const [fundTransferDate, setFundTransferDate] = React.useState<Date | null>(
    fundingComponent.fundTransferDate
      ? commonDate.fullDateStrToDate(fundingComponent.fundTransferDate)
      : new Date(),
  );

  const [
    markLenderShareComponentFundTransferDate,
    { loading: isLoading },
  ] = useMarkLenderShareComponentFundTransferDateMutation({
    refetchQueries: [
      GetResolvedLoanByIdDocumentGQLType,
      GetDailyAccountActivityDocumentGQLType,
      GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
    ],
  });

  const handleMarkAsFundedClick = async (): Promise<void> => {
    if (!fundTransferDate) {
      toast({
        ...successToastConfig,
        title: 'Lender funded date not selected',
      });
      return;
    }

    const { fundingRequestComponentId, lenderShareId } = fundingComponent;

    try {
      await markLenderShareComponentFundTransferDate({
        variables: {
          lenderShareId,
          fundingRequestComponentId,
          fundTransferDate: commonDate.dateToFullDateString(fundTransferDate),
        },
      });
      toast({
        ...successToastConfig,
        title: `Lender share marked as funded.`,
      });
    } catch (error) {
      toast({
        ...errorToastAdminConfig,
        title:
          'If you are unable to resolve the error, contact the Finley support team for assistance.',
      });
    }
  };

  return (
    <Modal {...modalControls} size="lg">
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>
          <Typography variant="section-subtitle">
            {fundingComponent.fundTransferDate ? 'Change' : 'Select'} Lender
            Funded Date
          </Typography>
          <Typography variant="base-muted">
            {fundingComponent.fundTransferDate
              ? 'Selecting a past date for "Mark Funded" sets the Lender Funded Date and may impact accounting records. Ensure accuracy before proceeding.'
              : "This date will be saved as the 'Lender Funded Date'."}
          </Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleDatePicker
            minDate={
              commonUtils.flags.isAdminAgentFrontingEnabled
                ? commonDate.fullDateStrToDate(fundingRequest.originationDate)
                : new Date()
            }
            dateFormat="MMMM dd, yyyy"
            selectedDate={fundTransferDate}
            setSelectedDate={setFundTransferDate}
            excludeWeekends
            excludeHolidays
            width="full"
          />

          {!!fundTransferDate && fundTransferDate > new Date() && (
            <Alert status="info" mt="20px">
              <AlertIcon />
              <AlertDescription fontWeight="semibold">
                You have selected a future funding date.
              </AlertDescription>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter gap="0.5rem" mt="1rem">
          <Button
            colorScheme="v2-aurora"
            onClick={modalControls.onClose}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            colorScheme="v2-aurora"
            onClick={async () => {
              await handleMarkAsFundedClick();
              modalControls.onClose();
            }}
            isDisabled={!fundTransferDate}
            isLoading={isLoading}
          >
            {fundingComponent.fundTransferDate ? 'Apply Changes' : 'Submit'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
